<template>
    <font-awesome-icon v-if="isLoading" icon="spinner" spin/>
    <i v-else-if="!attributeValues.length">Geen informatie gevonden</i>
    <div v-else>
        <b-table :fields="productAttributeTableFields" :items="attributeValues" class="mt-2" small>
            <template #cell(value)="data">
                <component v-if="data.item.component" :is="data.item.component" :product-identifier="number"></component>
                <span v-else-if="data.item.product_attribute?.identifier === 'image_url'">
                    <b-link @click="isImageModalOpen = true">
                        <img :src="data.item.value" alt="" class="product-image"/>
                    </b-link>
                    <b-modal v-model="isImageModalOpen" :title="`Afbeelding voor product ${number}`" size="xl" hide-footer body-class="product-image-container">
                        <img :src="data.item.value" alt="" class="product-image-large"/>
                    </b-modal>
                </span>
                <span v-else v-html="data.value"/>
            </template>
        </b-table>
    </div>
</template>

<script>
import {isFeatureEnabled} from "@/helpers/permissions";
import ProductInfoSupplyChain from "@/components/ProductInfoSupplyChain";

export default {
    name: 'ProductInfo',

    components: {ProductInfoSupplyChain},

    props: {
        number: {required: true, type: String},
        productName: {required: false, type: String},
        dispatchRequestLine: {required: false, type: Object},
        salesOrderLine: {required: false, type: Object},
        showOnlyFields: {required: false, type: Array},
    },

    mounted() {
        this.$store.getters.getProductInfo(this.number)
            .finally(() => this.isLoading = false)
    },

    data() {
        return {
            isLoading: true,
            isImageModalOpen: false,
            productAttributeTableFields: [
                {
                    key: 'attribute_name',
                    label: 'Eigenschap',
                    formatter: (value, key, item) => {
                        return item.product_attribute.display_name
                    },
                },
                {
                    key: 'value',
                    label: 'Waarde',
                },
            ],
            rulesets: [],
        }
    },

    computed: {
        displayProductName() {
            return this.productName
                ?? this.dispatchRequestLine?.product_name
                ?? this.dispatchRequestLine?.sales_order_line?.product_name
                ?? this.salesOrderLine?.product_name
        },
        displayEans() {
            return this.dispatchRequestLine?.ean_codes?.join(' ')
                ?? this.dispatchRequestLine?.sales_order_line?.ean_codes
                ?? this.salesOrderLine?.ean_codes
        },
        product() {
            return this.$store.state.products[this.number]
        },
        enabledAttributes() {
            return this.$store.getters.getFrontendParameterValue('product-info', 'modal-show-fields')
        },
        attributeValues() {
            const attributeValues = structuredClone(this.product?.attribute_values) ?? []
            let key;

            if (!this.enabledAttributes.includes('weight') || this.isFieldExcluded('weight')) {
                key = attributeValues.findIndex(val => val.product_attribute?.identifier === 'product_weight_in_grams')
                if (key !== -1) attributeValues.splice(key, 1)
            }
            if (!this.enabledAttributes.includes('image') || this.isFieldExcluded('image')) {
                key = attributeValues.findIndex(val => val.product_attribute?.identifier === 'image_url')
                if (key !== -1) attributeValues.splice(key, 1)
            }
            if (!this.enabledAttributes.includes('dimensions') || this.isFieldExcluded('dimensions')) {
                key = attributeValues.findIndex(val => val.product_attribute?.identifier === 'product_length_in_cm')
                if (key !== -1) attributeValues.splice(key, 1)
                key = attributeValues.findIndex(val => val.product_attribute?.identifier === 'product_width_in_cm')
                if (key !== -1) attributeValues.splice(key, 1)
                key = attributeValues.findIndex(val => val.product_attribute?.identifier === 'product_height_in_cm')
                if (key !== -1) attributeValues.splice(key, 1)
            }

            if (this.enabledAttributes.includes('name') && this.displayProductName && !this.isFieldExcluded('name')) {
                attributeValues.unshift({
                    value: this.displayProductName,
                    product_attribute: {
                        display_name: this.$t('frontend-parameters.parameters.product-info.modal-show-fields.options.name'),
                    },
                })
            }

            if (this.enabledAttributes.includes('identifier') && !this.isFieldExcluded('identifier')) {
                attributeValues.unshift({
                    value: this.number,
                    product_attribute: {
                        display_name: this.$t('frontend-parameters.parameters.product-info.modal-show-fields.options.identifier'),
                    },
                })
            }

            if (this.enabledAttributes.includes('barcodes') && this.displayEans && !this.isFieldExcluded('barcodes')) {
                attributeValues.push({
                    value: this.displayEans?.replace(' ', '<br/>'),
                    product_attribute: {
                        display_name: this.$t('frontend-parameters.parameters.product-info.modal-show-fields.options.barcodes'),
                    },
                })
            }

            if (this.enabledAttributes.includes('ruleset') && isFeatureEnabled('supply-chain-management') && !this.isFieldExcluded('ruleset')) {
                attributeValues.push({
                    value: null,
                    component: 'ProductInfoSupplyChain',
                    product_attribute: {
                        display_name: this.$t('frontend-parameters.parameters.product-info.modal-show-fields.options.ruleset'),
                    },
                })
            }

            return attributeValues
        },
    },

    methods: {
        isFieldExcluded(field) {
            if (!this.showOnlyFields?.length) {
                return false;
            }

            return !this.showOnlyFields.includes(field)
        },
    },
}
</script>

<style scoped>
img.product-image {
    max-width: 80px;
    max-height: 80px;
}
img.product-image-large {
    display: block;
    margin: 0 auto;
}
</style>

<style>
.product-image-container {
    overflow: scroll;
}
</style>
